import AttachmentType from 'owa-service/lib/contract/AttachmentType';
import AttendeeType from 'owa-service/lib/contract/AttendeeType';
import BodyContentType from 'owa-service/lib/contract/BodyContentType';
import BusyType from 'owa-service/lib/contract/BusyType';
import CalendarEventKey from './CalendarEventKey';
import CalendarItemExperienceType from 'owa-service/lib/contract/CalendarItemExperienceType';
import CalendarItemTypeType from 'owa-service/lib/contract/CalendarItemTypeType';
import EffectiveRightsType from 'owa-service/lib/contract//EffectiveRightsType';
import EnhancedLocation from 'owa-service/lib/contract/EnhancedLocation';
import InboxReminderType from 'owa-service/lib/contract/InboxReminderType';
import ItemId from 'owa-service/lib/contract/ItemId';
import MultiDayInfo, { MultiDayEventInstanceType } from './MultiDayInfo';
import RecurrenceType from 'owa-service/lib/contract/RecurrenceType';
import ResponseTypeType from 'owa-service/lib/contract/ResponseTypeType';
import SensitivityType from 'owa-service/lib/contract/SensitivityType';
import SingleRecipientType from 'owa-service/lib/contract/SingleRecipientType';
import TimeConstants from 'mini-datetime-utils/lib/dateValues/TimeConstants';
import { ClientFolderId, ClientItemId } from 'owa-client-ids';
import { getTimestamp, now, OwaDate } from 'owa-datetime';
import { isProviderTypeTeams } from './utils/isProviderTypeTeams';
import { OnlineMeetingProviderType } from './OnlineMeetingProviderType';

export declare type CalendarItemTypeTypeEx =
    | CalendarItemTypeType
    | 'ThisAndFollowingInstancesMaster';

/* tslint:disable:variable-name */
export default class CalendarEvent {
    ItemId: ClientItemId;
    AssociatedMailItemId?: ClientItemId;
    ParentFolderId: ClientFolderId;
    Subject: string;
    ReminderIsSet: boolean;
    ReminderMinutesBeforeStart: number;
    LastModifiedTime: OwaDate;
    InstanceKey: string;
    Preview: string;
    EffectiveRights: EffectiveRightsType;
    HasAttachments: boolean;
    Attachments: AttachmentType[];
    Sensitivity: SensitivityType;
    Body: BodyContentType;
    EntityNamesMap: number;
    IsDraft: boolean;
    Categories: string[];
    __type: string;
    Start: OwaDate;
    End: OwaDate;
    IsAllDayEvent: boolean;
    FreeBusyType: BusyType;
    IsMeeting: boolean;
    IsCancelled: boolean;
    IsRecurring: boolean;
    IsResponseRequested: boolean;
    CalendarItemType: CalendarItemTypeTypeEx;
    ResponseType: ResponseTypeType;
    Organizer: SingleRecipientType;
    RequiredAttendees: AttendeeType[];
    OptionalAttendees: AttendeeType[];
    Resources: AttendeeType[];
    AppointmentReplyTime: OwaDate;
    Recurrence: RecurrenceType;
    Location: EnhancedLocation;
    IntendedFreeBusyStatus: BusyType;
    IsOrganizer: boolean;
    AppointmentReplyName: string;
    IsSeriesCancelled: boolean;
    CharmId: number;
    Locations: EnhancedLocation[];
    ClientSeriesId: string;
    UID: string;
    ExperienceType: CalendarItemExperienceType;
    DoNotForwardMeeting: boolean;
    IsOnlineMeeting: boolean;
    JoinOnlineMeetingUrl: string;
    InboxReminders: InboxReminderType[];
    MeetingRequestWasSent: boolean;
    ExtractionSourceId: ItemId;
    StartTimeZoneId: string;
    EndTimeZoneId: string;
    OnlineMeetingProvider: OnlineMeetingProviderType | undefined;
    OnlineMeetingJoinUrl: string;
    OnlineMeetingConferenceId: string;
    OnlineMeetingTollNumber: string;
    OnlineMeetingTollFreeNumbers: string[];
    SeriesMasterItemId: ItemId;
    ExtractionSourceInternetMessageId: string;
    Key: CalendarEventKey;
    MultiDayInfo: MultiDayInfo;
    ConflictingMeetings: CalendarEvent[];
    AdjacentMeetings: CalendarEvent[];
    ProposedNewTimeStart: OwaDate;
    ProposedNewTimeEnd: OwaDate;

    /* Here we list all the computed properties */

    // General Computed properties
    /**
     * Returns true if the event can qualify to be an all day event
     */
    get IsEffectiveAllDayEvent(): boolean {
        return !!(
            this.IsAllDayEvent ||
            (this.MultiDayInfo &&
                this.MultiDayInfo.InstanceType === MultiDayEventInstanceType.MIDDLE)
        );
    }

    /**
     * Returns the duration of the event in milliseconds
     * @param forCompleteExpansion If true, returns the duration of the whole event for a multiday event, including its expansion instances
     * @param tillEndOfExpansion  If true, the duration will be from the start of the current expansion instance till the end. Eg: For event from 1pm today till 3 pm day after tomorrow, if this param is true and we are on the second instance, it will return
     * 15+24 = 39 hrs as the duration ( in miiliseconds )
     */
    durationInMs(
        forCompleteExpansion: boolean = false,
        tillEndOfExpansion: boolean = false
    ): number {
        let end: number = getTimestamp(this.End);
        let start: number = getTimestamp(this.Start);

        if (this.MultiDayInfo) {
            if (forCompleteExpansion && tillEndOfExpansion) {
                throw new Error('Ambiguous choice');
            }

            end =
                forCompleteExpansion || tillEndOfExpansion
                    ? getTimestamp(this.MultiDayInfo.OriginalEventEnd)
                    : end;
            start = forCompleteExpansion
                ? getTimestamp(this.MultiDayInfo.OriginalEventStart)
                : start;
        }

        return end - start;
    }

    /**
     * Returns true if the event is a private event and false otherwise
     */
    get IsPrivateEvent(): boolean {
        return this.Sensitivity === 'Private';
    }

    /**
     * Returns true if the event has categories
     */
    get HasCategories(): boolean {
        return !!(this.Categories && this.Categories.length);
    }

    // Computed Properties related to Time
    /**
     * Determines if an event originally occured in the past
     */
    get IsPastEvent(): boolean {
        return (
            getTimestamp(now()) >
            getTimestamp((this.MultiDayInfo && this.MultiDayInfo.OriginalEventEnd) || this.End)
        );
    }

    /**
     * Determines if an event instance occured in the past
     */
    get IsPastEventInstance(): boolean {
        return getTimestamp(now()) > getTimestamp(this.End);
    }

    /**
     * Determines if this original event is not upcoming nor ongoing and occurs in the future
     */
    get IsFutureEvent(): boolean {
        const currentTime: number = getTimestamp(now());

        return (
            getTimestamp(
                (this.MultiDayInfo && this.MultiDayInfo.OriginalEventStart) || this.Start
            ) > currentTime
        );
    }

    /**
     * Determines if this INSTANCE of the event is not upcoming nor ongoing and occurs in the future
     */
    get IsFutureEventInstance(): boolean {
        const currentTime: number = getTimestamp(now());

        return getTimestamp(this.Start) > currentTime;
    }

    /**
     * Determines if an event INSTANCE is Already Happening
     */
    get IsOngoingEvent(): boolean {
        const currentTimestamp: number = getTimestamp(now());

        const lowerBound: number = getTimestamp(
            (this.MultiDayInfo && this.MultiDayInfo.OriginalEventStart) || this.Start
        );
        const upperBound: number = getTimestamp(
            (this.MultiDayInfo && this.MultiDayInfo.OriginalEventEnd) || this.End
        );

        return lowerBound <= currentTimestamp && currentTimestamp <= upperBound;
    }

    /**
     * Determines if an event INSTANCE is Already Happening
     */
    get IsOngoingEventInstance(): boolean {
        const currentTimestamp: number = getTimestamp(now());

        const lowerBound: number = getTimestamp(this.Start);
        const upperBound: number = getTimestamp(this.End);

        return lowerBound <= currentTimestamp && currentTimestamp <= upperBound;
    }

    /**
     * Determines if an event is Upcoming within the next isStartingWithin ms
     * @param isStartingWithin the number of milliseconds within which to look up for an upcoming event. Pass 0 or lesses for infinite lookup
     */
    IsUpcomingEvent(isStartingWithin: number): boolean {
        const currentTimestamp: number = getTimestamp(now());

        const upperBound: number = getTimestamp(
            (this.MultiDayInfo && this.MultiDayInfo.OriginalEventStart) || this.Start
        );
        const lowerBound: number = isStartingWithin <= 0 ? 0 : upperBound - isStartingWithin;

        return lowerBound <= currentTimestamp && currentTimestamp < upperBound;
    }

    /**
     * Determines if an event INSTANCE is Upcoming within the next isStartingWithin ms
     * @param isStartingWithin the number of milliseconds within which to look up for an upcoming event. Pass 0 or lesses for infinite lookup
     */
    IsUpcomingEventInstance(isStartingWithin: number): boolean {
        const currentTimestamp: number = getTimestamp(now());

        const upperBound: number = getTimestamp(this.Start);
        const lowerBound: number = isStartingWithin <= 0 ? 0 : upperBound - isStartingWithin;

        return lowerBound <= currentTimestamp && currentTimestamp < upperBound;
    }

    /**
     * Determines if an event INSTANCE is starting after the timestamp passed
     * @param cutoffTimeInMs
     */
    IsInstanceStartingAfter(cutoffTimeInMs: number): boolean {
        return getTimestamp(this.Start) > cutoffTimeInMs;
    }

    /**
     * Determines if an event is starting at the passed cutoff
     * @param cutoffTimeInMs
     */
    IsStartingAt(cutoffTimeInMs: number): boolean {
        const eventStartTime: number = getTimestamp(
            (this.MultiDayInfo && this.MultiDayInfo.OriginalEventStart) || this.Start
        );

        return eventStartTime === cutoffTimeInMs;
    }

    /**
     * Determines the time remaining for an Event to complete
     */
    get RemainingTimeToEventEnd(): number {
        const currentTime: number = getTimestamp(now());

        const endTimeStamp: number = getTimestamp(
            (this.MultiDayInfo && this.MultiDayInfo.OriginalEventEnd) || this.End
        );

        return endTimeStamp <= currentTime ? 0 : endTimeStamp - currentTime;
    }

    /**
     * Determines the time remaining for an Event Instance to complete
     */
    get RemainingTimeToEventInstanceEnd(): number {
        const currentTime: number = getTimestamp(now());

        const endTimeStamp: number = getTimestamp(this.End);

        return endTimeStamp <= currentTime ? 0 : endTimeStamp - currentTime;
    }

    /**
     * Determines the time remaining for an event to Start
     */
    get RemainingTimeToEventStart(): number {
        const currentTime: number = getTimestamp(now());

        const startTimeStamp: number = getTimestamp(
            (this.MultiDayInfo && this.MultiDayInfo.OriginalEventStart) || this.Start
        );

        return startTimeStamp <= currentTime ? 0 : startTimeStamp - currentTime;
    }

    /**
     * Determines the time remaining for an event instance to Start
     */
    get RemainingTimeToEventInstanceStart(): number {
        const currentTime: number = getTimestamp(now());

        const startTimeStamp: number = getTimestamp(this.Start);

        return startTimeStamp <= currentTime ? 0 : startTimeStamp - currentTime;
    }

    /**
     * Determines the time remaining for an event to become upcoming ( isStartingWithin ms away )
     */
    RemainingTimeToBecomeUpcomingEvent(isStartingWithin: number): number {
        const currentTime: number = getTimestamp(now());

        let startTimeStamp: number = getTimestamp(
            (this.MultiDayInfo && this.MultiDayInfo.OriginalEventStart) || this.Start
        );

        startTimeStamp -= isStartingWithin;

        return startTimeStamp <= currentTime ? 0 : startTimeStamp - currentTime;
    }

    /**
     * Determines the time remaining for an event instance to become upcoming ( isStartingWithin ms away )
     */
    RemainingTimeToBecomeUpcomingEventInstance(isStartingWithin: number): number {
        const currentTime: number = getTimestamp(now());

        let startTimeStamp: number = getTimestamp(this.Start);

        startTimeStamp -= isStartingWithin;

        return startTimeStamp <= currentTime ? 0 : startTimeStamp - currentTime;
    }

    // Instance methods
    /**
     * This method determines if the event passed as param has its instance start and end time within or overlapping this event's current instance or not
     * This returns false if the event is exactly overlapping this event
     */
    instanceContainsEventInstance(event: CalendarEvent): boolean {
        const thisEventStart: number = getTimestamp(this.Start);
        const thisEventEnd: number = getTimestamp(this.End);
        const eventStart: number = getTimestamp(event.Start);
        const eventEnd: number = getTimestamp(event.End);

        return containsComparator(thisEventStart, thisEventEnd, eventStart, eventEnd);
    }

    /**
     * This method determines if the event passed as param has its instance start and end time within or overlapping this event in original time or not
     * This returns false if the event is exactly overlapping this event
     */
    containsEventInstance(event: CalendarEvent): boolean {
        const thisEventStart: number = getTimestamp(
            (this.MultiDayInfo && this.MultiDayInfo.OriginalEventStart) || this.Start
        );
        const thisEventEnd: number = getTimestamp(
            (this.MultiDayInfo && this.MultiDayInfo.OriginalEventEnd) || this.End
        );
        const eventStart: number = getTimestamp(event.Start);
        const eventEnd: number = getTimestamp(event.End);

        return containsComparator(thisEventStart, thisEventEnd, eventStart, eventEnd);
    }

    /**
     * This method determines if the event passed as param has its original start and end time within or overlapping this event in its current instance time or not
     * This returns false if the event is exactly overlapping this event
     */
    instanceContainsEvent(event: CalendarEvent): boolean {
        const eventStart: number = getTimestamp(
            (event.MultiDayInfo && event.MultiDayInfo.OriginalEventStart) || event.Start
        );
        const eventEnd: number = getTimestamp(
            (event.MultiDayInfo && event.MultiDayInfo.OriginalEventEnd) || event.End
        );
        const thisEventStart: number = getTimestamp(this.Start);
        const thisEventEnd: number = getTimestamp(this.End);

        return containsComparator(thisEventStart, thisEventEnd, eventStart, eventEnd);
    }

    /**
     * This method determines if the event passed as param has its original start and end time within or overlapping this event in its original time or not
     * This returns false if the event is exactly overlapping this event
     */
    containsEvent(event: CalendarEvent): boolean {
        const eventStart: number = getTimestamp(
            (event.MultiDayInfo && event.MultiDayInfo.OriginalEventStart) || event.Start
        );
        const eventEnd: number = getTimestamp(
            (event.MultiDayInfo && event.MultiDayInfo.OriginalEventEnd) || event.End
        );
        const thisEventStart: number = getTimestamp(
            (this.MultiDayInfo && this.MultiDayInfo.OriginalEventStart) || this.Start
        );
        const thisEventEnd: number = getTimestamp(
            (this.MultiDayInfo && this.MultiDayInfo.OriginalEventEnd) || this.End
        );

        return containsComparator(thisEventStart, thisEventEnd, eventStart, eventEnd);
    }

    /**
     * Returns true if the passed event and this event start at the same time to the nearest minute
     * @param event
     */
    startsAlongWith(event: CalendarEvent): boolean {
        const eventStart: number = getTimestamp(event.Start);
        const thisEventStart: number = getTimestamp(this.Start);

        // We can't have events starting between minutes ideally because we can't create
        // events starting between minutes, but better safe than sorry since timeStamps can differ slightly
        // hence that is fixed here
        return (
            Math.floor(eventStart / TimeConstants.MillisecondsIn1Min) ===
            Math.floor(thisEventStart / TimeConstants.MillisecondsIn1Min)
        );
    }

    /**
     * Returns true if the passed event and this event end at the same time to the nearest minute
     * @param event
     */
    endsAlongWith(event: CalendarEvent): boolean {
        const eventEnd: number = getTimestamp(event.End);
        const thisEventEnd: number = getTimestamp(this.End);

        // We can't have events ending between minutes ideally because we can't create
        // events ending between minutes, but better safe than sorry since timeStamps can differ slightly
        // hence that is fixed here
        return (
            Math.floor(eventEnd / TimeConstants.MillisecondsIn1Min) ===
            Math.floor(thisEventEnd / TimeConstants.MillisecondsIn1Min)
        );
    }

    /**
     * Returns true if the user can respond to a given event.
     */
    canRespond(): boolean {
        return !this.IsOrganizer && this.IsMeeting && !this.IsCancelled && this.canModify();
    }

    /**
     * Returns true if the user can modify a given event.
     */
    canModify(): boolean {
        return !!this.EffectiveRights?.Modify;
        /*
            TODO: Additional checks needed. Skipping for now.
            !isReadOnlyLocalCalendar(item.ParentFolderId.Id) &&
            !isNewCalendarItem(item) &&
            !(isMeetingOnCalendarSharedWithMe(item) && item.Sensitivity == 'Private')
            */
    }

    canEdit(): boolean {
        return this.IsOrganizer && this.canModify();
    }

    isMeetingAttendee(): boolean {
        return this.IsMeeting && !this.IsOrganizer;
    }

    /**
     * Determines if the delete button is to be shown for attendee
     */
    canDeleteAsAttendee(): boolean {
        return this.isMeetingAttendee() && this.canModify() && !this.IsCancelled;
    }

    canCancel(): boolean {
        return !!(
            this.IsOrganizer &&
            this.isEventAMeeting() &&
            this.MeetingRequestWasSent &&
            this.canModify()
        );
    }

    canDeleteAsOrganizer(): boolean {
        return !!(
            this.IsOrganizer &&
            (!this.isEventAMeeting() || !this.MeetingRequestWasSent) &&
            this.canModify()
        );
    }

    /**
     * Determines if the delete button is to be shown
     */
    canDelete(): boolean {
        return this.canDeleteAsOrganizer() || this.canDeleteAsAttendee();
    }

    /**
     * Determines if the delete button is to be shown
     */
    canRemove(): boolean {
        return !this.IsOrganizer && this.IsCancelled && this.canModify();
    }

    /**
     *
     * Determines if this item can be forwarded
     */
    canReply(): boolean {
        return this.IsMeeting;
    }

    /**
     *
     * Determines if this item can be forwarded
     */
    canForward(): boolean {
        // TODO: Additional calendar specific logic
        //     !(
        //         [
        //             CalendarFolderTypeEnum.BirthdayCalendar,
        //             CalendarFolderTypeEnum.BookingCalendar,
        //             CalendarFolderTypeEnum.SchedulesCalendar,
        //         ].includes(calendarEntry.CalendarFolderType) || calendarItem.IsMeetingPollEvent
        //     )
        return (
            this.canModify() &&
            this.IsMeeting &&
            (this.IsOrganizer || !this.DoNotForwardMeeting) &&
            !this.IsCancelled
        );
    }

    /**
     * Returns true if the event has attendees (whether required, optional or resources)
     * Filters out the organizer if present in the attendee list
     * @param ignoreResources Filters out the resources (when it's true)
     */
    hasAttendees(ignoreResources?: boolean): boolean {
        const removeOrganizerIfNeeded = (attendees: AttendeeType[]): AttendeeType[] =>
            attendees
                ? attendees.filter(
                      attendee =>
                          !this.Organizer ||
                          attendee.Mailbox.EmailAddress !== this.Organizer.Mailbox.EmailAddress
                  )
                : [];

        const requiredAttendees = removeOrganizerIfNeeded(this.RequiredAttendees).length;
        const optionalAttendees = removeOrganizerIfNeeded(this.OptionalAttendees).length;

        if (ignoreResources) {
            return requiredAttendees + optionalAttendees > 0;
        }

        const resources = removeOrganizerIfNeeded(this.Resources).length;

        return requiredAttendees + optionalAttendees + resources > 0;
    }

    /**
     * Returns true if the event has atleast one non-null location
     */
    hasLocations(): boolean {
        return !!this.Locations && this.Locations.filter(location => !!location).length > 0;
    }

    /**
     * Returns true if the calendar event is a meeting
     */
    isEventAMeeting(): boolean {
        return this.IsMeeting || this.hasAttendees();
    }

    /**
     * Returns true if the calendar event has valid online meeting details
     */
    isValidOnlineMeeting(): boolean {
        return this.IsOnlineMeeting && !!this.OnlineMeetingJoinUrl;
    }

    /**
     * Returns true if the event is part of a series but not the master
     */
    isSeriesInstance(): boolean {
        return (
            this.IsRecurring &&
            this.CalendarItemType !== 'RecurringMaster' &&
            this.CalendarItemType !== 'ThisAndFollowingInstancesMaster'
        );
    }

    /**
     * Returns true if the event is the master of a recurrence
     */
    isSeriesMaster(): boolean {
        return this.CalendarItemType === 'RecurringMaster';
    }

    /**
     * Returns true if this event is an normal instance in a series
     */
    get IsSeriesInstanceOccurrence(): boolean {
        return this.CalendarItemType === 'Occurrence';
    }

    /**
     * Returns true if this event is an exception instance in a series
     */
    get IsSeriesInstanceException(): boolean {
        return this.CalendarItemType === 'Exception';
    }

    /**
     * Returns true if the event is a special series master for
     * ThisAndFollowingInstances scope
     */
    isFTDFSeriesMaster(): boolean {
        return this.CalendarItemType === 'ThisAndFollowingInstancesMaster';
    }

    /**
     * Returns the actual count of conflicting events
     */
    get ActualConflictingMeetingCount(): number {
        return getCountOfValidEntries(this.ConflictingMeetings, this);
    }

    /**
     * Returns the next valid conflicting event
     */
    get NextConflictingEvent(): CalendarEvent {
        return getNextValidEntry(this.ConflictingMeetings, this);
    }

    /**
     * Returns the count of conflicting events
     */
    get ActualAdjacentMeetingCount(): number {
        return getCountOfValidEntries(this.AdjacentMeetings, this);
    }

    /**
     * Returns the next valid conflicting event
     */
    get NextAdjacentEvent(): CalendarEvent {
        return getNextValidEntry(this.AdjacentMeetings, this);
    }

    /**
     * returns true if the response is one of accepted, tentative, or declined
     */
    get IsRespondedEvent(): boolean {
        return ['Accept', 'Tentative', 'Decline'].includes(this.ResponseType);
    }

    /**
     * returns true if the current meeting is a teams meeting
     */
    get IsTeamsMeeting(): boolean {
        return isProviderTypeTeams(this.OnlineMeetingProvider);
    }
}

const getFilteredFutureEvents = (
    entries: CalendarEvent[],
    currentEvent: CalendarEvent
): CalendarEvent[] =>
    entries?.filter(
        (event: CalendarEvent) =>
            event?.FreeBusyType !== 'Free' &&
            event.ItemId?.Id &&
            event.ItemId?.Id !== currentEvent.ItemId?.Id
    );

const getCountOfValidEntries = (entries: CalendarEvent[], currentEvent: CalendarEvent): number =>
    getFilteredFutureEvents(entries, currentEvent)?.length || 0;

const getNextValidEntry = (entries: CalendarEvent[], currentEvent: CalendarEvent): CalendarEvent =>
    getFilteredFutureEvents(entries, currentEvent)?.[0];

function containsComparator(
    superEventStart: number,
    superEventEnd: number,
    subEventStart: number,
    subEventEnd: number
): boolean {
    if (superEventStart < subEventStart && superEventEnd > subEventEnd) {
        return true;
    }

    if (superEventStart === subEventStart) {
        return superEventEnd > subEventEnd; // no overlap
    }

    if (subEventEnd === superEventEnd) {
        return subEventStart > superEventStart; // no overlap
    }

    return false; // all other cases are of mismatch
}
